import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

import { BASE_URL } from "../utils/constants";
import { notify } from "../components/Notification";
import { getCsrfToken, fetchCsrfToken } from "./csrf";
import { recordRejectionStatus } from "./rejectionStatus";

/**
 * Type declaration to extend Axios config with request metadata
 */
declare module "axios" {
  export interface InternalAxiosRequestConfig {
    metadata?: { requestId: string; startTime: number };
    _retry?: boolean;
    _csrfRetry?: boolean;
  }
}

/**
 * Enum for token refresh result status
 */
enum RefreshStatus {
  SUCCESS = "success",
  FAILURE = "failure",
}

/**
 * Constants for authentication error handling
 */
const CSRF_ERROR_CODE = "CSRF_TOKEN_INVALID";
const AUTH_ERROR_DEBOUNCE_MS = 5000; // Prevent duplicate notifications within 5 seconds
const REDIRECT_DELAY_MS = 100; // Small delay before redirect to allow notification to show

const instance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Track refresh token state to prevent multiple simultaneous refresh attempts
 */
let isRefreshing = false;
let refreshSubscribers: Array<
  (result: RefreshStatus, errorMsg?: string) => void
> = [];
let lastAuthErrorTime = 0;

const subscribeTokenRefresh = (
  cb: (result: RefreshStatus, errorMsg?: string) => void,
) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (result: RefreshStatus, errorMsg?: string) => {
  refreshSubscribers.forEach((cb) => cb(result, errorMsg));
  refreshSubscribers = [];
};

/**
 * Check if a request is a session status check endpoint
 */
const isSessionCheck = (url?: string) => url?.includes("/auth/me");

/**
 * Helper function to handle authentication errors with appropriate notifications
 * Only shows ONE notification even if multiple requests fail within a 5-second window
 * @param refreshErrorMsg - The error message from the refresh attempt
 * @param wasSessionCheck - Whether the original request was a session status check
 */
const handleAuthError = (
  refreshErrorMsg: string | undefined,
  wasSessionCheck: boolean = false,
) => {
  // Don't notify/redirect for session check failures - they're expected when not logged in
  if (wasSessionCheck) {
    return;
  }

  const now = Date.now();

  // Prevent duplicate notifications within 5 seconds
  if (now - lastAuthErrorTime < AUTH_ERROR_DEBOUNCE_MS) {
    return;
  }
  lastAuthErrorTime = now;

  if (refreshErrorMsg === "Invalid Refresh Token") {
    notify.info("Your session expired. Please log in again.");
  } else {
    notify.error("Authentication failed. Please log in again.");
  }

  // Redirect to login page after a small delay
  setTimeout(() => {
    globalThis.window.location.href = "/login";
  }, REDIRECT_DELAY_MS);
};

/**
 * Request interceptor to add CSRF token and request tracking
 */
instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const method = config.method?.toUpperCase();

    // Add CSRF token to state-changing requests
    if (method && ["POST", "PUT", "DELETE", "PATCH"].includes(method)) {
      const token = getCsrfToken();
      if (token) {
        config.headers["x-csrf-token"] = token;
      }
    }

    // Add request tracking
    const requestId = crypto.randomUUID();
    config.headers["X-Request-ID"] = requestId;
    config.metadata = { requestId, startTime: Date.now() };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Response interceptor to handle token expiry, CSRF errors, and request logging
 */
instance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Log error requests with timing
    if (error.config?.metadata) {
      // Attach requestId to error for error boundary handling
      error.requestId = error.config.metadata.requestId;
    }
    const originalRequest = error.config;

    // Handle 401 Unauthorized (session expired) - Attempt token refresh
    if (isFirstUnauthorizedRequest(error, originalRequest)) {
      originalRequest._retry = true;
      const wasSessionCheck = isSessionCheck(originalRequest.url);

      // If already refreshing, wait for the existing refresh to complete
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((result, errorMsg) => {
            if (result === RefreshStatus.SUCCESS) {
              resolve(instance(originalRequest));
            } else {
              handleAuthError(errorMsg, wasSessionCheck);
              // Reject with ErrorResponse shape for consistent error handling
              // eslint-disable-next-line prefer-promise-reject-errors
              reject({ errors: [errorMsg || "Authentication failed"] });
            }
          });
        });
      }

      isRefreshing = true;

      try {
        const result = await axios.post(
          BASE_URL + "/auth/refreshToken",
          {},
          { withCredentials: true },
        );

        if (result.status === 200) {
          isRefreshing = false;
          onTokenRefreshed(RefreshStatus.SUCCESS);
          return instance(originalRequest);
        }

        // Handle unexpected non-200 status from refresh endpoint
        isRefreshing = false;
        const unexpectedErrorMsg = `Unexpected refresh response status: ${result.status}`;
        onTokenRefreshed(RefreshStatus.FAILURE, unexpectedErrorMsg);
        handleAuthError(unexpectedErrorMsg, wasSessionCheck);
        // Throw error based on refresh response, not original request error
        throw result.data ?? { errors: [unexpectedErrorMsg] };
      } catch (refreshError: any) {
        isRefreshing = false;
        const refreshErrorMsg = refreshError?.response?.data?.errors?.[0];

        // Notify subscribers (they'll handle auth errors independently)
        onTokenRefreshed(RefreshStatus.FAILURE, refreshErrorMsg);

        // Handle auth error for the initiating request
        handleAuthError(refreshErrorMsg, wasSessionCheck);
        throw refreshError.response?.data ?? error.response?.data ?? error;
      }
    }

    // Handle 403 Forbidden - CSRF token invalid/expired
    if (isFirstForbiddenRequest(error, originalRequest)) {
      originalRequest._csrfRetry = true;

      try {
        // Fetch new CSRF token
        await fetchCsrfToken();

        // Retry the original request with new token
        const token = getCsrfToken();
        if (token) {
          originalRequest.headers["x-csrf-token"] = token;
        }

        return instance(originalRequest);
      } catch (csrfError) {
        console.error("Failed to refresh CSRF token:", csrfError);
        throw rejectionOf(error);
      }
    }

    throw rejectionOf(error);
  },
);

const rejectionOf = (error: AxiosError) =>
  error.response
    ? recordRejectionStatus(error.response.data ?? error, error.response.status)
    : error;

const isFirstUnauthorizedRequest = (
  error: AxiosError,
  originalRequest: InternalAxiosRequestConfig,
) => {
  // Don't retry on refresh token endpoint or other auth mutation endpoints
  const isAuthMutationEndpoint =
    originalRequest.url?.includes("/auth/refreshToken") ||
    originalRequest.url?.includes("/auth/login") ||
    originalRequest.url?.includes("/auth/logout") ||
    originalRequest.url?.includes("/auth/register");

  return (
    error.response?.status === 401 &&
    !originalRequest._retry &&
    !isAuthMutationEndpoint
  );
};

const isFirstForbiddenRequest = (
  error: AxiosError,
  originalRequest: InternalAxiosRequestConfig,
) => {
  // Don't retry CSRF token fetch endpoint itself
  return (
    error.response?.status === 403 &&
    (error.response.data as { code?: string } | undefined)?.code ===
      CSRF_ERROR_CODE &&
    !originalRequest._csrfRetry &&
    !originalRequest.url?.includes("/csrf-token")
  );
};

export default instance;
